import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface ProfissionalCartoesProps {
  professionalId: string;
}

interface PaymentCard {
  id: string;
  last_four_digits: string;
  brand: string;
  holder_name: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
  pagarme_card_id: string;
}

export function ProfissionalCartoes({ professionalId }: ProfissionalCartoesProps) {
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState<PaymentCard[]>([]);
  const [deletingCard, setDeletingCard] = useState<PaymentCard | null>(null);

  useEffect(() => {
    loadCards();
  }, [professionalId]);

  const loadCards = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("payment_cards")
        .select("*")
        .eq("user_id", professionalId)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCards(data || []);
    } catch (error) {
      console.error("Error loading cards:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (cardId: string) => {
    try {
      // Remover default de todos
      await supabase
        .from("payment_cards")
        .update({ is_default: false })
        .eq("user_id", professionalId);

      // Definir novo default
      const { error } = await supabase
        .from("payment_cards")
        .update({ is_default: true })
        .eq("id", cardId);

      if (error) throw error;

      toast.success("Cartão padrão atualizado");
      loadCards();
    } catch (error) {
      toast.error("Erro ao definir cartão padrão");
    }
  };

  const handleDelete = async () => {
    if (!deletingCard) return;

    try {
      const { error } = await supabase.functions.invoke('pagarme-delete-card', {
        body: { card_id: deletingCard.pagarme_card_id }
      });

      if (error) throw error;

      toast.success("Cartão excluído com sucesso");
      setDeletingCard(null);
      loadCards();
    } catch (error: any) {
      toast.error("Erro ao excluir cartão");
    }
  };

  if (loading) {
    return <p>Carregando...</p>;
  }

  if (cards.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-center">Nenhum cartão cadastrado</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map((card) => (
          <Card key={card.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  <CardTitle className="text-base">{card.brand}</CardTitle>
                </div>
                {card.is_default && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Star className="h-3 w-3 fill-current" />
                    Padrão
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Final</p>
                <p className="font-medium">•••• {card.last_four_digits}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Titular</p>
                <p className="font-medium">{card.holder_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Validade</p>
                <p className="font-medium">
                  {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                </p>
              </div>
              <div className="flex gap-2 pt-2">
                {!card.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetDefault(card.id)}
                  >
                    <Star className="h-4 w-4 mr-2" />
                    Definir como Padrão
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeletingCard(card)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!deletingCard} onOpenChange={() => setDeletingCard(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cartão final{" "}
              <strong>{deletingCard?.last_four_digits}</strong>?
              <br />
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}